import React, { createContext, useContext, useEffect, useState } from "react";
import api from "../lib/api";

/**
 * Site-wide currency toggle.
 *
 * The CART, BACKEND, and DATABASE always store JMD (the source of truth).
 * This context only changes how prices are *displayed* on the customer-facing
 * side. The currency choice is persisted in localStorage so it sticks across
 * pages and reloads.
 */

const STORAGE_KEY = "fruitblossoms.currency";
const CurrencyContext = createContext(null);

export function CurrencyProvider({ children }) {
  const [currency, setCurrencyState] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || "JMD"; } catch { return "JMD"; }
  });
  const [rate, setRate] = useState(155);  // JMD per 1 USD — refreshed from /settings

  useEffect(() => {
    api.get("/settings")
      .then((r) => setRate(r.data.exchange_rate_jmd_per_usd || 155))
      .catch(() => {});
  }, []);

  const setCurrency = (c) => {
    setCurrencyState(c);
    try { localStorage.setItem(STORAGE_KEY, c); } catch {}
  };
  const toggle = () => setCurrency(currency === "JMD" ? "USD" : "JMD");

  const formatPrice = (jmdAmount) => {
    if (jmdAmount === null || jmdAmount === undefined || isNaN(jmdAmount)) return "—";
    if (currency === "USD") {
      const usd = jmdAmount / (rate || 155);
      return `US$${usd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return `JMD $${Number(jmdAmount).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  };

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, toggle, rate, formatPrice }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be used inside CurrencyProvider");
  return ctx;
}

/** Inline price renderer — use anywhere a price appears. */
export function Price({ jmd, className = "", testid }) {
  const { formatPrice } = useCurrency();
  return <span className={className} data-testid={testid}>{formatPrice(jmd)}</span>;
}

/** Hook returning a `fmtJMD(amount)`-compatible formatter that respects the
 * customer's currency choice. Drop-in replacement for `import { fmtJMD }` on
 * customer-facing pages. */
export function useFmt() {
  const { formatPrice } = useCurrency();
  return formatPrice;
}
